import {
    useEffect,
    useState,
} from "react";

import PlatformAuthContext
    from "./PlatformAuthContext";

import {
    platformLoginService,
    getPlatformProfileService,
} from "../../services/platformAuthService";

import {
    savePlatformToken,
    savePlatformRefreshToken,
    savePlatformUser,
    getPlatformToken,
    getPlatformUser,
    clearPlatformAuthStorage,
} from "../../utils/platformAuthStorage";


const PlatformAuthProvider = ({
    children,
}) => {

    /*
    |--------------------------------------------------------------------------
    | STATE
    |--------------------------------------------------------------------------
    */

    const [
        platformUser,
        setPlatformUser,
    ] =
        useState(
            null
        );

    const [
        platformToken,
        setPlatformToken,
    ] =
        useState(
            null
        );

    const [
        platformLoading,
        setPlatformLoading,
    ] =
        useState(
            true
        );

    const [
        isPlatformAuthenticated,
        setIsPlatformAuthenticated,
    ] =
        useState(
            false
        );


    /*
    |--------------------------------------------------------------------------
    | CLEAR SESSION
    |--------------------------------------------------------------------------
    */

    const clearPlatformSession =
        () => {

            clearPlatformAuthStorage();

            setPlatformUser(
                null
            );

            setPlatformToken(
                null
            );

            setIsPlatformAuthenticated(
                false
            );

        };


    /*
    |--------------------------------------------------------------------------
    | LOAD CURRENT PLATFORM ADMIN
    |--------------------------------------------------------------------------
    */

    const refreshPlatformProfile =
        async () => {

            try {

                const response =
                    await getPlatformProfileService();


                if (
                    response?.success !==
                        true &&
                    response?.status !==
                        "success"
                ) {

                    return {
                        success:
                            false,

                        message:
                            response?.message ||
                            "Unable to load platform profile.",

                        code:
                            response?.code ||
                            null,
                    };

                }


                const user =
                    response
                        ?.data
                        ?.user ||
                    null;


                if (
                    !user
                ) {

                    return {
                        success:
                            false,

                        message:
                            "Platform administrator profile was not returned.",
                    };

                }


                /*
                |--------------------------------------------------------------------------
                | Safety Check
                |--------------------------------------------------------------------------
                */

                if (
                    String(
                        user?.role ||
                        ""
                    )
                        .trim()
                        .toUpperCase() !==
                    "PLATFORM_SUPER_ADMIN"
                ) {

                    clearPlatformSession();


                    return {
                        success:
                            false,

                        message:
                            "Platform Super Admin access is required.",
                    };

                }


                savePlatformUser(
                    user
                );


                setPlatformUser(
                    user
                );


                setIsPlatformAuthenticated(
                    true
                );


                return {
                    success:
                        true,

                    user,
                };

            } catch (
                error
            ) {

                console.error(
                    "[PlatformAuthProvider] Profile error:",
                    error
                );


                return {
                    success:
                        false,

                    message:
                        error?.message ||
                        "Unable to load platform profile.",
                };

            }

        };


    /*
    |--------------------------------------------------------------------------
    | INITIALIZE PLATFORM SESSION
    |--------------------------------------------------------------------------
    */

    useEffect(
        () => {

            let mounted =
                true;


            const initializePlatformAuth =
                async () => {

                    try {

                        setPlatformLoading(
                            true
                        );


                        const storedToken =
                            getPlatformToken();


                        const storedUser =
                            getPlatformUser();


                        /*
                        |--------------------------------------------------------------------------
                        | NO PLATFORM TOKEN
                        |--------------------------------------------------------------------------
                        */

                        if (
                            !storedToken
                        ) {

                            if (
                                mounted
                            ) {

                                setPlatformUser(
                                    null
                                );

                                setPlatformToken(
                                    null
                                );

                                setIsPlatformAuthenticated(
                                    false
                                );

                            }

                            return;

                        }


                        /*
                        |--------------------------------------------------------------------------
                        | TEMP RESTORE
                        |--------------------------------------------------------------------------
                        */

                        if (
                            mounted
                        ) {

                            setPlatformToken(
                                storedToken
                            );

                            setPlatformUser(
                                storedUser
                            );

                        }


                        /*
                        |--------------------------------------------------------------------------
                        | VERIFY WITH BACKEND
                        |--------------------------------------------------------------------------
                        */

                        const response =
                            await getPlatformProfileService();


                        if (
                            !mounted
                        ) {
                            return;
                        }


                        if (
                            response?.success ===
                                true ||
                            response?.status ===
                                "success"
                        ) {

                            const user =
                                response
                                    ?.data
                                    ?.user;


                            /*
                            |--------------------------------------------------------------------------
                            | ROLE CHECK
                            |--------------------------------------------------------------------------
                            */

                            if (
                                String(
                                    user?.role ||
                                    ""
                                )
                                    .trim()
                                    .toUpperCase() !==
                                "PLATFORM_SUPER_ADMIN"
                            ) {

                                clearPlatformSession();

                                return;

                            }


                            savePlatformUser(
                                user
                            );


                            setPlatformUser(
                                user
                            );


                            setIsPlatformAuthenticated(
                                true
                            );


                            return;

                        }


                        /*
                        |--------------------------------------------------------------------------
                        | TOKEN INVALID
                        |--------------------------------------------------------------------------
                        */

                        if (
                            response?.code ===
                                401 ||
                            response?.code ===
                                403
                        ) {

                            clearPlatformSession();

                        }

                    } catch (
                        error
                    ) {

                        console.error(
                            "[PlatformAuthProvider] Initialization error:",
                            error
                        );

                    } finally {

                        if (
                            mounted
                        ) {

                            setPlatformLoading(
                                false
                            );

                        }

                    }

                };


            initializePlatformAuth();


            return () => {

                mounted =
                    false;

            };

        },
        []
    );


    /*
    |--------------------------------------------------------------------------
    | PLATFORM LOGIN
    |--------------------------------------------------------------------------
    */

    const platformLogin =
        async (
            payload
        ) => {

            try {

                setPlatformLoading(
                    true
                );


                /*
                |--------------------------------------------------------------------------
                | LOGIN
                |--------------------------------------------------------------------------
                */

                const response =
                    await platformLoginService(
                        payload
                    );


                if (
                    response?.success !==
                        true &&
                    response?.status !==
                        "success"
                ) {

                    return {
                        success:
                            false,

                        message:
                            response?.message ||
                            "Invalid platform administrator credentials.",
                    };

                }


                /*
                |--------------------------------------------------------------------------
                | RESPONSE
                |--------------------------------------------------------------------------
                */

                const accessToken =
                    response
                        ?.data
                        ?.accessToken ||
                    response
                        ?.data
                        ?.token ||
                    null;


                const refreshToken =
                    response
                        ?.data
                        ?.refreshToken ||
                    null;


                const loginUser =
                    response
                        ?.data
                        ?.user ||
                    null;


                /*
                |--------------------------------------------------------------------------
                | ACCESS TOKEN REQUIRED
                |--------------------------------------------------------------------------
                */

                if (
                    !accessToken
                ) {

                    return {
                        success:
                            false,

                        message:
                            "Platform access token was not returned.",
                    };

                }


                /*
                |--------------------------------------------------------------------------
                | SAVE TOKEN BEFORE /ME
                |--------------------------------------------------------------------------
                |
                | /me requires platformAccessToken.
                |--------------------------------------------------------------------------
                */

                savePlatformToken(
                    accessToken
                );


                if (
                    refreshToken
                ) {

                    savePlatformRefreshToken(
                        refreshToken
                    );

                }


                if (
                    loginUser
                ) {

                    savePlatformUser(
                        loginUser
                    );

                }


                setPlatformToken(
                    accessToken
                );


                /*
                |--------------------------------------------------------------------------
                | VERIFY PROFILE
                |--------------------------------------------------------------------------
                */

                const profileResponse =
                    await getPlatformProfileService();


                if (
                    profileResponse?.success !==
                        true &&
                    profileResponse?.status !==
                        "success"
                ) {

                    clearPlatformSession();


                    return {
                        success:
                            false,

                        message:
                            profileResponse?.message ||
                            "Unable to verify platform administrator session.",
                    };

                }


                const user =
                    profileResponse
                        ?.data
                        ?.user ||
                    loginUser;


                /*
                |--------------------------------------------------------------------------
                | ROLE CHECK
                |--------------------------------------------------------------------------
                */

                if (
                    String(
                        user?.role ||
                        ""
                    )
                        .trim()
                        .toUpperCase() !==
                    "PLATFORM_SUPER_ADMIN"
                ) {

                    clearPlatformSession();


                    return {
                        success:
                            false,

                        message:
                            "Platform Super Admin access is required.",
                    };

                }


                /*
                |--------------------------------------------------------------------------
                | SUCCESS
                |--------------------------------------------------------------------------
                */

                savePlatformUser(
                    user
                );


                setPlatformUser(
                    user
                );


                setIsPlatformAuthenticated(
                    true
                );


                return {
                    success:
                        true,

                    user,
                };

            } catch (
                error
            ) {

                console.error(
                    "[PlatformAuthProvider] Login error:",
                    error
                );


                clearPlatformSession();


                return {
                    success:
                        false,

                    message:
                        error?.message ||
                        "Platform login failed.",
                };

            } finally {

                setPlatformLoading(
                    false
                );

            }

        };


    /*
    |--------------------------------------------------------------------------
    | LOGOUT
    |--------------------------------------------------------------------------
    |
    | Your currently shared platform auth routes expose login + me only,
    | therefore logout is client-side for now.
    |--------------------------------------------------------------------------
    */

    const platformLogout =
        () => {

            clearPlatformSession();

        };


    /*
    |--------------------------------------------------------------------------
    | VALUE
    |--------------------------------------------------------------------------
    */

    return (

        <PlatformAuthContext.Provider
            value={{

                platformUser,

                platformToken,

                platformLoading,

                isPlatformAuthenticated,

                platformLogin,

                platformLogout,

                refreshPlatformProfile,

                setPlatformUser,

                setPlatformToken,

            }}
        >

            {children}

        </PlatformAuthContext.Provider>

    );

};


export default PlatformAuthProvider;